import * as Sentry from '@sentry/react';
import { scrubSentryEvent } from './sentryScrubbing';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Tenants exclus du Session Replay (démo, staff, test — pollution quota).
// Les erreurs/issues restent capturées (via @sentry/react standard),
// seul le replay vidéo-reconstitué est skippé pour ces tenants.
const REPLAY_EXCLUDED_TENANTS = new Set<string>([
  'a4af8c06-ab99-48c2-905a-fd734bff52e1', // Boulangerie Demo VisionPOS
  'e7a6ba42-9ace-448b-b402-5784b53fc95f', // Demo Vision
  'd89f652a-7fc7-448d-9711-7272eaf3d27b', // test
  '6a6b51bb-f8bd-4c38-b10c-e3e6cdbb2756', // Vision POS Test Mode
  'd9253beb-4130-4a95-b208-f407dad133a3', // VisionPOS Staff
]);

function isReplayExcludedTenant(): boolean {
  try {
    const scope = Sentry.getCurrentScope();
    const tenantId = scope?.getScopeData()?.tags?.tenant_id as string | undefined;
    return !!tenantId && REPLAY_EXCLUDED_TENANTS.has(tenantId);
  } catch {
    return false;
  }
}

// Sprint remote-ops I: Sentry DSN obligatoire en prod.
// Sans DSN, un bug sur device revendeur est aveugle — inacceptable pour du SaaS facturé.
// En dev/preview, on warn simplement pour ne pas bloquer le flux de travail.
export function initSentry() {
  // Garde idempotente : le client Sentry vit sur le carrier global (globalThis.__SENTRY__),
  // donc partagé même si ce module est évalué deux fois (ex. bundle chargé sous deux
  // origines — www.visionpos.fr servant le HTML et pos.visionpos.fr les assets). Un second
  // Sentry.init() ferait lever « Multiple Sentry Session Replay instances are not supported »
  // et planterait l'app dans l'ErrorBoundary.
  if (Sentry.getClient()) {
    return;
  }
  if (!SENTRY_DSN) {
    if (import.meta.env.PROD) {
      const msg = '[Sentry] DSN manquant en production — VITE_SENTRY_DSN doit être défini au build';
      console.error(msg);
      try {
        const marker = document.createElement('div');
        marker.id = 'sentry-config-warning';
        marker.style.cssText =
          'position:fixed;bottom:0;left:0;right:0;z-index:99999;background:#b91c1c;color:#fff;padding:6px;font-family:monospace;font-size:11px;text-align:center;opacity:0.9';
        marker.textContent = 'Monitoring Sentry non configuré — contactez le support VisionPos';
        const mount = () => document.body && document.body.appendChild(marker);
        if (document.body) mount();
        else document.addEventListener('DOMContentLoaded', mount);
      } catch {
        /* body pas encore prêt */
      }
      return;
    }
    console.warn('[Sentry] DSN non configure - monitoring desactive (mode dev)');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: 'visionpos-360@' + (import.meta.env.VITE_APP_VERSION || '1.0.0'),
      tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
      // Session Replay — sampling fin pour tenir dans le quota Free (50 replays/mois).
      // - session: 0 = jamais de replay "aléatoire" sur session normale
      // - onError: 0.5 = 50% des sessions qui déclenchent une erreur → vidéo capturée
      // Le callback beforeErrorSampling exclut en plus les tenants demo/staff.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.5,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          beforeErrorSampling: () => !isReplayExcludedTenant(),
        }),
      ],
      // Évite l'envoi des credentials par défaut (cookies, headers Authorization)
      sendDefaultPii: false,
      beforeSend(event) {
        // Filtre les network errors silencieux (offline, fetch abort)
        if (
          event.exception?.values?.[0]?.type === 'TypeError' &&
          event.exception.values[0].value?.includes('Failed to fetch')
        ) {
          return null;
        }
        // Scrubbing PII (invariant 4.A.15) — PAN/CVV/JWT/password/email
        // Voir src/lib/sentryScrubbing.ts pour la liste exhaustive des patterns.
        return scrubSentryEvent(event);
      },
      initialScope: {
        tags: {
          app: 'visionpos-360',
          build_target: import.meta.env.VITE_BUILD_TARGET || 'web',
        },
      },
    });
  } catch (e) {
    // Le monitoring ne doit jamais empêcher la caisse de démarrer.
    console.warn('[Sentry] init ignoré', e);
  }
}

export function setSentryUser(user: {
  id: string;
  email?: string;
  tenantId?: string;
  tenantSlug?: string;
}) {
  // Invariant 4.A.15 — pas de PII en logs Sentry.
  // On garde uniquement l'id technique (UUID Supabase auth) — le mapping
  // id → email/identité reste côté Supabase, accessible aux super_admin
  // pour le debug ciblé. Le paramètre `email` est ignoré volontairement.
  Sentry.setUser({ id: user.id });
  if (user.tenantId) {
    Sentry.setTag('tenant_id', user.tenantId);
  }
  if (user.tenantSlug) {
    Sentry.setTag('tenant_slug', user.tenantSlug);
  }
}

export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTag('tenant_id', undefined as unknown as string);
  Sentry.setTag('tenant_slug', undefined as unknown as string);
}

export { Sentry };
